import {
  gapVerdict,
  type LayoutSibling,
  periodLabel,
  totalGap,
} from '@/modules/projects/components/reconciliation/helpers'
import { isReconciled } from '@/modules/projects/status'
import type {
  ReextractSnapshot,
  StatementSegment,
} from '@/modules/projects/stores'
import { numberToCurrency } from '@/utils'

export const FixedSiblingList = ({
  siblings,
}: {
  siblings: LayoutSibling[]
}) => (
  <section className="space-y-1.5">
    <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
      {caption(siblings)}
    </p>
    <ul className="max-h-48 divide-y overflow-auto rounded-lg border bg-white">
      {siblings.map(({ statement, segment }) => (
        <li
          key={segment.id}
          className="flex items-center justify-between gap-4 px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-sm text-foreground">
              {statement.fileName}
            </p>
            <p className="text-xs text-muted-foreground">
              {periodLabel(segment)}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs text-muted-foreground tabular-nums">
              {beforeVerdict(segment.beforeReextract)}
            </p>
            <CurrentVerdict segment={segment} />
          </div>
        </li>
      ))}
    </ul>
  </section>
)

const CurrentVerdict = ({ segment }: { segment: StatementSegment }) => {
  if (isReconciled(segment)) {
    return <p className="text-xs font-medium text-emerald-600">Balanced</p>
  }
  const gap = totalGap(segment.reconciliationDetail?.accounts ?? [])
  return (
    <p className="text-xs font-medium tabular-nums text-amber-700">
      {gapVerdict(gap)}
    </p>
  )
}

// A cycle can need review with a zero gap when extra rows cancel each other out, so a gap of zero
// never prints an amount.
const beforeVerdict = (snapshot: ReextractSnapshot | null | undefined) => {
  const gap = totalGap(snapshot?.accounts ?? [])
  return gap > 0 ? `Was off by ${numberToCurrency(gap)}` : 'Needed review'
}

// "Fixed" is only true once every one of them balances; until then the list reports the run.
const caption = (siblings: LayoutSibling[]) => {
  const count = siblings.length
  const others = count === 1 ? '1 other statement' : `${count} other statements`
  const allBalanced = siblings.every(({ segment }) => isReconciled(segment))
  return allBalanced
    ? `We also fixed ${others}`
    : `We ran this fix on ${others}`
}
